import { useLocation, useParams } from "react-router-dom";

import type { ScheduleDetail } from "@/api/schedule/models";
import { Button, Icon } from "@/components/common";
import { AsideMenuTalentLayout } from "@/components/common/navigation/AsideMenuTalent/AsideMenuTalentLayout";
import { TalentCardLayout } from "@/components/features/talent/TalentCardLayout";
import { useDepartmentLink } from "@/hooks/common/use-department-link";
import { useDepartmentRoute } from "@/hooks/common/use-department-route";
import { useUserStore } from "@/stores/use-user-store";
import { copyToClipboard, toast } from "@/utils";
import { getScheduleRegistrationLink } from "@/utils/schedule-registration-link";
import { getUserRoleByDepartment } from "@/utils/user-positions";
import { UserRoleEnum } from "@/api/user/models";
import { buildRoute, type RoutePathEnum as RoutePath } from "@/routes/paths";

import { ScheduleActions } from "../ScheduleActions";
import { ScheduleStatusTag } from "../ScheduleStatusTag";

type ScheduleDetailAsideProps = {
  isLoading: boolean;
  navigationItems: ReadonlyArray<{ label: string; path: RoutePath }>;
  schedule?: ScheduleDetail;
};

export const ScheduleDetailAside = ({ isLoading, navigationItems, schedule }: ScheduleDetailAsideProps) => {
  const { pathname } = useLocation();
  const { scheduleId } = useParams<{ scheduleId: string }>();
  const { department } = useDepartmentRoute();
  const activeUser = useUserStore((state) => state.activeUser);

  const buildDepartmentLink = useDepartmentLink();

  const routes = navigationItems.map(({ label, path }) => ({
    name: label,
    path: buildDepartmentLink(buildRoute(path, { scheduleId: scheduleId ?? "" })),
  }));
  const role = getUserRoleByDepartment(activeUser, department);
  const canShareRegistrationLink =
    !!schedule &&
    [UserRoleEnum.ADMINISTRATOR, UserRoleEnum.COORDINATOR, UserRoleEnum.RECEPTIONIST].includes(role as UserRoleEnum) &&
    ["NEW_CANDIDATE", "PHOTO_RENEWAL"].includes(schedule.scheduleType) &&
    !["CANCELED", "COMPLETED"].includes(schedule.status) &&
    (schedule.currentStage === "SCHEDULED" || schedule.isPresent);

  const handleCopyRegistrationLink = async () => {
    if (!scheduleId) {
      return;
    }
    try {
      await copyToClipboard(
        await getScheduleRegistrationLink(scheduleId),
        "Link da ficha cadastral copiada com sucesso!",
      );
    } catch {
      toast.error("Erro ao gerar link da ficha cadastral.");
    }
  };

  return (
    <AsideMenuTalentLayout
      actions={
        schedule && (
          <ScheduleActions.Provider schedule={schedule}>
            <div className="flex flex-col gap-2">
              {canShareRegistrationLink && (
                <Button variant="secondary" full onClick={handleCopyRegistrationLink}>
                  <Icon name="Copy" size="1.25rem" />
                  Ficha cadastral
                </Button>
              )}
              <ScheduleActions.Start />
              <ScheduleActions.Resume />
              <ScheduleActions.Complete />
              <ScheduleActions.MoveStage />
              <ScheduleActions.Reschedule />
              <ScheduleActions.Delete />
              <ScheduleActions.Pause />
              <ScheduleActions.Unpause />
              <ScheduleActions.Cancel />
            </div>
          </ScheduleActions.Provider>
        )
      }
      basePathname=""
      card={
        <TalentCardLayout
          title={schedule?.participant.fullName ?? ""}
          subtitle={schedule?.participant.age ? `${schedule.participant.age} anos` : null}
          imageUrl={schedule?.participant.picture.url}
          imageAlt={schedule?.participant.fullName ?? "Talento"}
        />
      }
      header={schedule && <ScheduleStatusTag schedule={schedule} />}
      isLoading={isLoading}
      pathname={pathname}
      routes={routes}
    />
  );
};
